import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Handshake, Edit3, Save, Loader2, Plus, Trash2, Sparkles } from "lucide-react";

const toLines = (a) => (Array.isArray(a) ? a.join("\n") : (a || ""));
const fromLines = (s) => String(s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const eur0 = (v) => (v != null && v !== "" ? `€ ${Number(v).toLocaleString("it-IT")}` : "—");

const Field = ({ label, value }) => (
  <div>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="whitespace-pre-line text-sm font-medium">{value || "—"}</div>
  </div>
);

const EMPTY = {
  tipo_mandato: "", esclusiva: false, zona_note: "",
  provvigione_materiale_pct: "", provvigione_impianti_pct: "",
  rimborsi_previsti: false, rimborsi_elenco: [], extra_economici: [],
  inclusioni: "", esclusioni: "", obiettivo_annuo: "",
  condizioni_economiche: "", note_relazione: "", clausole_rilevanti: "",
};

const List = ({ items, render }) => (
  (items && items.length) ? <ul className="mt-1 space-y-0.5 text-sm">{items.map((it, i) => <li key={i} className="flex gap-1.5"><span className="text-primary">•</span><span>{render(it)}</span></li>)}</ul> : <div className="text-sm text-muted-foreground">—</div>
);

export default function AgencyCondizioni({ agencyId, relazione, canEdit, prefill, openSignal, onSaved }) {
  const rel = relazione || {};
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [fromPdf, setFromPdf] = useState(false);

  const load = (base) => setForm({
    ...EMPTY, ...base,
    provvigione_materiale_pct: base.provvigione_materiale_pct ?? "",
    provvigione_impianti_pct: base.provvigione_impianti_pct ?? "",
    obiettivo_annuo: base.obiettivo_annuo ?? "",
    inclusioni: toLines(base.inclusioni), esclusioni: toLines(base.esclusioni),
    clausole_rilevanti: toLines(base.clausole_rilevanti),
    rimborsi_elenco: base.rimborsi_elenco || [], extra_economici: base.extra_economici || [],
  });

  useEffect(() => {
    if (openSignal > 0 && prefill) { load({ ...rel, ...prefill }); setFromPdf(true); setOpen(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal]);

  const openEdit = () => { load(rel); setFromPdf(false); setOpen(true); };
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setItem = (key, i, k, v) => setForm((s) => { const a = [...(s[key] || [])]; a[i] = { ...a[i], [k]: v }; return { ...s, [key]: a }; });
  const addItem = (key, obj) => setForm((s) => ({ ...s, [key]: [...(s[key] || []), obj] }));
  const delItem = (key, i) => setForm((s) => ({ ...s, [key]: (s[key] || []).filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        provvigione_materiale_pct: form.provvigione_materiale_pct === "" ? null : Number(form.provvigione_materiale_pct),
        provvigione_impianti_pct: form.provvigione_impianti_pct === "" ? null : Number(form.provvigione_impianti_pct),
        obiettivo_annuo: form.obiettivo_annuo === "" ? null : Number(form.obiettivo_annuo),
        inclusioni: fromLines(form.inclusioni),
        esclusioni: fromLines(form.esclusioni),
        clausole_rilevanti: fromLines(form.clausole_rilevanti),
        rimborsi_elenco: (form.rimborsi_elenco || []).filter((r) => r.voce),
        extra_economici: (form.extra_economici || []).filter((r) => r.descrizione),
      };
      await api.put(`/agencies/${agencyId}/relazione`, { relazione: payload });
      toast.success("Condizioni salvate");
      setOpen(false); onSaved?.();
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="agency-conditions-section" className="p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-base font-bold tracking-tight sm:text-lg"><Handshake className="h-5 w-5 text-primary" />Condizioni della relazione</h2>
        {canEdit && <Button variant="ghost" size="sm" onClick={openEdit} data-testid="edit-conditions-button"><Edit3 className="mr-1.5 h-4 w-4" />Modifica</Button>}
      </div>
      <div className="grid gap-x-6 gap-y-4 md:grid-cols-2">
        <div className="space-y-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-primary">Inquadramento e mandato</div>
          <Field label="Tipo mandato" value={rel.tipo_mandato} />
          <Field label="Esclusiva territoriale" value={rel.esclusiva ? "Sì" : "No"} />
          <Field label="Zona / territorio" value={rel.zona_note} />
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Inclusioni</div>
            <List items={rel.inclusioni} render={(x) => x} />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Esclusioni</div>
            <List items={rel.esclusioni} render={(x) => x} />
          </div>
        </div>
        <div className="space-y-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-primary">Condizioni economiche</div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Provvigione materiale" value={rel.provvigione_materiale_pct != null ? `${rel.provvigione_materiale_pct}%` : "—"} />
            <Field label="Provvigione impianti" value={rel.provvigione_impianti_pct != null ? `${rel.provvigione_impianti_pct}%` : "—"} />
          </div>
          <Field label="Obiettivo annuo" value={eur0(rel.obiettivo_annuo)} />
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Rimborsi mensili {rel.rimborsi_previsti ? "" : "(non previsti)"}</div>
            <List items={rel.rimborsi_elenco} render={(r) => `${r.voce}${r.importo != null ? ` — ${eur0(r.importo)}` : ""}${r.note ? ` (${r.note})` : ""}`} />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Extra economici</div>
            <List items={rel.extra_economici} render={(r) => `${r.descrizione}${r.importo != null ? ` — ${eur0(r.importo)}` : ""}`} />
          </div>
          <Field label="Condizioni economiche particolari" value={rel.condizioni_economiche} />
        </div>
      </div>
      {(rel.clausole_rilevanti && rel.clausole_rilevanti.length > 0) && (
        <div className="mt-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Clausole rilevanti</div>
          <List items={rel.clausole_rilevanti} render={(x) => x} />
        </div>
      )}
      {rel.note_relazione && <div className="mt-4"><Field label="Note sulla relazione" value={rel.note_relazione} /></div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="conditions-edit-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2">Condizioni della relazione {fromPdf && <Badge className="border-primary/30 bg-primary/15 text-primary"><Sparkles className="mr-1 h-3 w-3" />Precompilato dal PDF</Badge>}</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Tipo mandato</Label>
              <Select value={form.tipo_mandato || ""} onValueChange={(v) => set("tipo_mandato", v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="cond-tipo-mandato"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monomandatario">Monomandatario</SelectItem>
                  <SelectItem value="plurimandatario">Plurimandatario</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={!!form.esclusiva} onCheckedChange={(v) => set("esclusiva", v)} data-testid="cond-esclusiva" />
              <Label className="text-xs">Esclusiva territoriale</Label>
            </div>
            <div className="sm:col-span-2"><Label className="text-xs">Zona / territorio</Label><Input value={form.zona_note || ""} onChange={(e) => set("zona_note", e.target.value)} className="mt-1 h-9" /></div>
            <div><Label className="text-xs">Provvigione materiale %</Label><Input type="number" step="0.1" value={form.provvigione_materiale_pct} onChange={(e) => set("provvigione_materiale_pct", e.target.value)} className="mt-1 h-9" data-testid="cond-provv-materiale" /></div>
            <div><Label className="text-xs">Provvigione impianti %</Label><Input type="number" step="0.1" value={form.provvigione_impianti_pct} onChange={(e) => set("provvigione_impianti_pct", e.target.value)} className="mt-1 h-9" data-testid="cond-provv-impianti" /></div>
            <div><Label className="text-xs">Obiettivo annuo (€)</Label><Input type="number" value={form.obiettivo_annuo} onChange={(e) => set("obiettivo_annuo", e.target.value)} className="mt-1 h-9" /></div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={!!form.rimborsi_previsti} onCheckedChange={(v) => set("rimborsi_previsti", v)} data-testid="cond-rimborsi-previsti" />
              <Label className="text-xs">Rimborsi mensili previsti</Label>
            </div>
          </div>

          <div className="mt-2">
            <div className="mb-1 flex items-center justify-between"><Label className="text-xs">Elenco rimborsi</Label><Button size="sm" variant="outline" onClick={() => addItem("rimborsi_elenco", { voce: "", importo: "", note: "" })}><Plus className="mr-1 h-3.5 w-3.5" />Voce</Button></div>
            {(form.rimborsi_elenco || []).map((r, i) => (
              <div key={i} className="mb-1 grid grid-cols-[1fr_7rem_1fr_auto] items-center gap-2">
                <Input placeholder="Voce (es. Pasti)" value={r.voce || ""} onChange={(e) => setItem("rimborsi_elenco", i, "voce", e.target.value)} className="h-8" />
                <Input placeholder="€" type="number" value={r.importo ?? ""} onChange={(e) => setItem("rimborsi_elenco", i, "importo", e.target.value === "" ? "" : Number(e.target.value))} className="h-8" />
                <Input placeholder="Note" value={r.note || ""} onChange={(e) => setItem("rimborsi_elenco", i, "note", e.target.value)} className="h-8" />
                <Button size="icon" variant="ghost" onClick={() => delItem("rimborsi_elenco", i)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>

          <div className="mt-2">
            <div className="mb-1 flex items-center justify-between"><Label className="text-xs">Extra economici</Label><Button size="sm" variant="outline" onClick={() => addItem("extra_economici", { descrizione: "", importo: "" })}><Plus className="mr-1 h-3.5 w-3.5" />Voce</Button></div>
            {(form.extra_economici || []).map((r, i) => (
              <div key={i} className="mb-1 grid grid-cols-[1fr_8rem_auto] items-center gap-2">
                <Input placeholder="Descrizione" value={r.descrizione || ""} onChange={(e) => setItem("extra_economici", i, "descrizione", e.target.value)} className="h-8" />
                <Input placeholder="€" type="number" value={r.importo ?? ""} onChange={(e) => setItem("extra_economici", i, "importo", e.target.value === "" ? "" : Number(e.target.value))} className="h-8" />
                <Button size="icon" variant="ghost" onClick={() => delItem("extra_economici", i)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>

          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <div><Label className="text-xs">Inclusioni (una per riga)</Label><Textarea value={form.inclusioni} onChange={(e) => set("inclusioni", e.target.value)} className="mt-1" rows={4} /></div>
            <div><Label className="text-xs">Esclusioni (una per riga)</Label><Textarea value={form.esclusioni} onChange={(e) => set("esclusioni", e.target.value)} className="mt-1" rows={4} /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Condizioni economiche particolari</Label><Textarea value={form.condizioni_economiche || ""} onChange={(e) => set("condizioni_economiche", e.target.value)} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Clausole rilevanti (una per riga)</Label><Textarea value={form.clausole_rilevanti} onChange={(e) => set("clausole_rilevanti", e.target.value)} className="mt-1" rows={4} /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Note sulla relazione</Label><Textarea value={form.note_relazione || ""} onChange={(e) => set("note_relazione", e.target.value)} className="mt-1" /></div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="save-conditions">{saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
